import { useState, useEffect, useCallback } from 'react';
import { Building2, Users, Store, TrendingUp, Shield, Activity, Wrench, Rocket, Palette } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent } from '@/components/ui/card';
import { TenantDetailView } from './super-admin/TenantDetailView';
import { TenantListView } from './super-admin/TenantListView';
import { ActivityLogView } from './super-admin/ActivityLogView';
import { SuperAdminsView } from './super-admin/SuperAdminsView';
import { DeploymentChangelogView } from './super-admin/DeploymentChangelogView';
import { PlatformBrandingView } from './super-admin/PlatformBrandingView';

function StatCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-600',
    blue: 'bg-info/10 text-info',
    emerald: 'bg-success/10 text-success',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantNotes, setTenantNotes] = useState([]);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [deleteUser, setDeleteUser] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [activeTab, setActiveTab] = useState('tenants');
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        api.get('/admin/tenants'),
        api.get('/admin/dashboard'),
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchActivityFeed = useCallback(async (tenantFilter) => {
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (tenantFilter && tenantFilter !== 'all') params.append('tenant_id', tenantFilter);
      const [feedRes, statsRes] = await Promise.all([
        api.get(`/admin/activity-feed?${params}`),
        api.get('/admin/activity-feed/stats?days=7'),
      ]);
      setActivityFeed(feedRes.data.logs || []);
      setActivityStats(statsRes.data);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivityFeed(activityFilter);
  }, [activeTab, activityFilter, fetchActivityFeed]);

  const openTenantDetail = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const [usersRes, notesRes] = await Promise.all([
        api.get(`/admin/tenants/${tenant.id}/users`).catch(() => ({ data: [] })),
        api.get(`/admin/tenants/${tenant.id}/notes`).catch(() => ({ data: [] })),
      ]);
      setTenantUsers(usersRes.data);
      setTenantNotes(notesRes.data);
    } catch {
      setTenantUsers([]);
      setTenantNotes([]);
    }
  };

  const toggleTenant = async (tenantId, currentStatus) => {
    try {
      await api.put(`/admin/tenants/${tenantId}`, { is_active: !currentStatus });
      toast.success(`Tenant ${!currentStatus ? 'activated' : 'suspended'}`);
      fetchData();
    } catch {
      toast.error('Failed to update tenant');
    }
  };

  const toggleInternal = async (tenantId, currentInternal) => {
    try {
      await api.put(`/admin/tenants/${tenantId}`, { is_internal: !currentInternal });
      toast.success(!currentInternal ? 'Marked as Internal Company (free, full access)' : 'Removed internal status');
      fetchData();
    } catch {
      toast.error('Failed to update tenant');
    }
  };

  const impersonateTenant = async (tenant) => {
    try {
      const res = await api.post(`/admin/tenants/${tenant.id}/impersonate`);
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('original_user', localStorage.getItem('user'));
      localStorage.setItem('impersonate_tenant_name', tenant.company_name);
      localStorage.setItem('impersonate_tenant_id', tenant.id);
      localStorage.setItem('token', res.data.access_token);
      toast.success(`Entering ${tenant.company_name} workspace...`);
      window.location.href = '/';
    } catch {
      toast.error('Failed to enter tenant workspace');
    }
  };

  const resetUserPassword = async () => {
    if (!showResetPw || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/admin/users/${showResetPw.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${showResetPw.email}. They'll be prompted to change it.`);
      setShowResetPw(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
  };

  const updateUser = async () => {
    if (!editUser) return;
    try {
      await api.put(`/admin/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditUser(null);
      if (selectedTenant) openTenantDetail(selectedTenant);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      toast.success(`User ${deleteUser.email} deleted`);
      setDeleteUser(null);
      if (selectedTenant) openTenantDetail(selectedTenant);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedTenant) return;
    try {
      await api.post(`/admin/tenants/${selectedTenant.id}/notes`, {
        content: newNote.trim(),
        category: noteCategory,
      });
      setNewNote('');
      const res = await api.get(`/admin/tenants/${selectedTenant.id}/notes`);
      setTenantNotes(res.data);
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  const deleteNote = async (noteId) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/admin/tenants/${selectedTenant.id}/notes/${noteId}`);
      setTenantNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const filtered = tenants.filter(t =>
    t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (selectedTenant) {
    return (
      <DashboardLayout>
        <TenantDetailView
          tenant={selectedTenant}
          tenantUsers={tenantUsers}
          tenantNotes={tenantNotes}
          onBack={() => setSelectedTenant(null)}
          onImpersonate={impersonateTenant}
          onToggleInternal={toggleInternal}
          onToggleActive={toggleTenant}
          showResetPw={showResetPw}
          setShowResetPw={setShowResetPw}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          onResetPassword={resetUserPassword}
          editUser={editUser}
          setEditUser={setEditUser}
          editForm={editForm}
          setEditForm={setEditForm}
          onUpdateUser={updateUser}
          deleteUser={deleteUser}
          setDeleteUser={setDeleteUser}
          onDeleteUser={handleDeleteUser}
          newNote={newNote}
          setNewNote={setNewNote}
          noteCategory={noteCategory}
          setNoteCategory={setNoteCategory}
          onAddNote={addNote}
          onDeleteNote={deleteNote}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-1" data-testid="super-admin-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-200/50">
            <Shield size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="super-admin-title">Platform Administration</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage tenants, provide support, and monitor usage</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => window.location.assign('/tenant-migration')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
              data-testid="open-tenant-migration-btn"
            >
              <Wrench size={16} /> Tenant Data Repair
            </button>
            <PageTour storageKey="tour_superadmin" steps={[
              { target: '[data-testid="stat-total-tenants"]', title: 'Tenant Overview', content: 'See total tenants, users, active accounts, and new registrations at a glance.' },
              { target: '[data-testid="admin-tabs"]', title: 'Admin Tabs', content: 'Switch between tenant management and platform activity logs.' },
              { target: '[data-testid="impersonate-btn"]', title: 'Impersonation', content: 'Log in as any tenant to troubleshoot issues or provide hands-on support.' },
            ]} />
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Building2} label="Total Tenants" value={stats.total_tenants} color="orange" testId="stat-total-tenants" />
            <StatCard icon={Users} label="Total Users" value={stats.total_users} color="blue" testId="stat-total-users" />
            <StatCard icon={Store} label="Active Tenants" value={stats.active_tenants} color="emerald" testId="stat-active-tenants" />
            <StatCard icon={TrendingUp} label="This Month" value={stats.new_this_month || 0} color="violet" testId="stat-new-month" />
          </div>
        )}

        <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit" data-testid="admin-tabs-2">
          {[
            { id: 'tenants', label: 'Tenants', icon: Building2 },
            { id: 'super_admins', label: 'Super Admins', icon: Shield },
            { id: 'changelog', label: 'Changelog', icon: Rocket },
            { id: 'activity', label: 'Activity Log', icon: Activity },
            { id: 'platform_branding', label: 'Platform Branding', icon: Palette },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'tenants' && (
          <TenantListView
            filtered={filtered}
            search={search}
            setSearch={setSearch}
            onOpenDetail={openTenantDetail}
            onImpersonate={impersonateTenant}
            onToggle={toggleTenant}
          />
        )}

        {activeTab === 'super_admins' && <SuperAdminsView />}

        {activeTab === 'changelog' && <DeploymentChangelogView />}

        {activeTab === 'platform_branding' && <PlatformBrandingView />}

        {activeTab === 'activity' && (
          <ActivityLogView
            feed={activityFeed}
            stats={activityStats}
            loading={activityLoading}
            filter={activityFilter}
            setFilter={setActivityFilter}
            tenants={tenants}
            onRefresh={() => fetchActivityFeed(activityFilter)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
